/**
 * useLiveStreamingAgora — the Agora-media core of the community live-streaming
 * page.
 *
 * Owns the role-based Agora session the Flutter `LiveStreamingScreenState` owned
 * (`lib/presentation/screens/community/live_streaming_screen.dart`), mapped onto
 * `agora-rtc-react`'s declarative hooks and scoped to the media half only:
 *   - role-based token mint (`generateLiveStreamingToken`, `:577-586`) —
 *     broadcaster gets `LiveStreamingRole.broadcaster`, audience gets
 *     `LiveStreamingRole.audience`;
 *   - join on the broadcaster's channel + `setClientRole('host')` for the
 *     broadcaster / `'audience'` for the audience (`initStreaming`, `:293-320`);
 *   - publish (broadcaster only — the audience publishes nothing, `:322-331`);
 *   - the join-success "resolved-uid dance" — fire `onChannelConnected` with the
 *     RESOLVED local uid, never the `0` auto-assign sentinel (`:421-445`,
 *     finding 4 ported from `useVideoCall`);
 *   - the token-renewal lifecycle (`:451-508`);
 *   - the broadcaster media controls (`_streamActions`, `:1464-1635`).
 *
 * This hook is intentionally UI-FREE (architecture-review Must Fix): it NEVER
 * calls `toast.*` and NEVER touches navigation directly. Terminal errors are
 * surfaced through the page-supplied `onJoinError` / `onTokenRenewError`
 * callbacks (the page owns the toast) and the page-supplied `goBack` (the page
 * owns the pop) — the enforced sibling being `useCommunity` exposing
 * `onLikeMessageError` while `CommunityPage` owns the actual `toast`. It does NOT
 * port `useVideoCall`'s in-hook `toast.error` — that is the older escape this
 * rule supersedes.
 *
 * `useBasePage` stays in the page (`presentation.md` — page-only). This hook is a
 * feature sub-hook: it exposes `onAgoraInit` / `onAgoraDispose` lifecycle bridges
 * the Task-10 orchestration hook composes into the page's `useBasePage`, plus the
 * media handles, control actions, and lifecycle state the page renders. It is
 * split out of the main `useLiveStreaming` hook so each stays within the
 * granularity budget (mirrors the `useCommunity` / `useCommunityLiveListener`
 * precedent).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import AgoraRTC, {
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
  useClientEvent,
} from 'agora-rtc-react';
import type {
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react';

import { LiveStreamingRole } from '@/domain/enums/liveStreaming/liveStreamingRole';
import { generateLiveStreamTokenUseCase } from '@/domain/usecases/videoCall/generateLiveStreamTokenUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * Flutter joins with `uid: _liveStreamingUserId`, which is `0` until join
 * success (`live_streaming_screen.dart:572`). Agora treats `uid: 0` as
 * "auto-assign"; the token is minted for the same `0` uid (`:580`), and the real
 * local uid is read off the connection afterwards (`connection.localUid!`,
 * `:426`) — the resolved-uid dance below.
 */
const LOCAL_STREAMING_UID = 0;

export interface UseLiveStreamingAgoraArgs {
  /** The Agora channel id — the broadcaster's Firebase uid (`:571`). */
  channel: string;
  /**
   * Whether the current user is the broadcaster (`_isBroadcaster`, `:149-151`).
   * Derived once by the Task-10 orchestration hook from the current uid vs
   * `broadcasterFirebaseId`; passed in so this media hook needs no Redux access.
   */
  isBroadcaster: boolean;
  /**
   * Page-supplied pop. The hook never reaches into routing itself (navigation is
   * the page's concern); it calls this on the terminal paths that used to call
   * `onBackPress()` directly: the initial-token-fetch failure (`:208`) and a
   * token-regen failure (`:476`, `:503`). Should be stable (page-memoized) so the
   * hook's dep arrays do not churn.
   */
  goBack: () => void;
  /**
   * Page-supplied. Fired when the session cannot be entered — the initial token
   * mint returned null/no-token (`:204-210`). The PAGE fires the "could not join
   * the Live session" toast; the hook stays UI-free. (Flutter's separate
   * `initStreaming` engine-init failure toast, `:302-306`, has no `useJoin`
   * equivalent that throws separately on the web — that path collapses into this
   * single session-entry-failure surface.)
   */
  onJoinError?: () => void;
  /**
   * Page-supplied. Fired when a token could not be regenerated mid-session
   * (`:472-477`, `:498-505`). The PAGE fires the "could not generate a new
   * streaming token" toast; the hook stays UI-free.
   */
  onTokenRenewError?: () => void;
  /**
   * Page-supplied. Fired exactly once at join success with the RESOLVED local
   * uid (never the `0` sentinel) — the web analog of Flutter's
   * `connection.localUid!` (`:426`). The broadcaster persists this as
   * `broadcasterStreamingId` (`:431-437`); the audience uses it as its
   * "connected" signal to bump the watcher count (`:439`). Should be
   * page-memoized.
   */
  onChannelConnected?: (localUid: number) => void;
  /**
   * Page-supplied. Fired for every Agora `user-left` with the leaving uid
   * (`onUserOffline`, `:520-524`). The page matches it against
   * `broadcasterStreamingId` to drive the "streaming ended" state. Should be
   * page-memoized.
   */
  onRemoteUserOffline?: (uid: number) => void;
}

export interface UseLiveStreamingAgoraResult {
  // --- Lifecycle state (the page's state-view gates) ---
  /** True until the channel is open (`_isOpeningChannel`, `:425`, `:1471`). */
  isOpeningChannel: boolean;
  /** `_isRenewingLiveStreamingToken` — drives the "regenerating token" overlay. */
  isRenewingToken: boolean;

  // --- Agora media handles (Task 11 video view) ---
  /** The broadcaster's local camera track (null for the audience). */
  localCameraTrack: ICameraVideoTrack | null;
  /** The broadcaster's local microphone track (null for the audience). */
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  /** The remote users in the channel (audience selects the broadcaster by uid). */
  remoteUsers: IAgoraRTCRemoteUser[];

  // --- Broadcaster control state + actions (Task 12 controls) ---
  /** `_isMute` (`muteLocalAudioStream(!_isMute)`, `:1508-1509`). */
  isMute: boolean;
  /** `_isCameraOff` (`muteLocalVideoStream(!_isCameraOff)`, `:1552-1553`). */
  isCameraOff: boolean;
  /** `_clearScreen` — hides the controls/overlays on a video tap (`:1469`). */
  clearScreen: boolean;
  /** Toggle mic mute via `localMicrophoneTrack.setMuted` (`:1508`). */
  toggleMute: () => Promise<void>;
  /** Toggle camera via `localCameraTrack.setEnabled` (`:1552`). */
  toggleCamera: () => Promise<void>;
  /** Best-effort switch camera over the once-enumerated device list (`:1485`). */
  switchCamera: () => Promise<void>;
  /** Toggle `_clearScreen`. */
  toggleClearScreen: () => void;
  /**
   * Number of available cameras, enumerated once by this hook. Task-12 controls
   * gate the switch-camera button on this (disabled when `< 2`).
   */
  cameraCount: number;

  // --- Lifecycle bridge (the page forwards into `useBasePage` via Task 10) ---
  /**
   * `useBasePage.onInit` target — runs the mount-once initial token fetch
   * (`init`, `:201-217`). Returns the init-side teardown `useBasePage` runs on
   * unmount (cancels a pending fetch).
   */
  onAgoraInit: () => () => void;
  /**
   * `useBasePage.onDispose` target — the media half of `dispose` /
   * `disposeStreaming` (`:220-254`): release the local tracks + `client.leave()`
   * (idempotent). The Firestore end-side (`endLiveMessage` / `setLiveWatchers`)
   * is the Task-10 orchestration hook's concern.
   */
  onAgoraDispose: () => void;
}

export const useLiveStreamingAgora = (
  args: UseLiveStreamingAgoraArgs,
): UseLiveStreamingAgoraResult => {
  const {
    channel,
    isBroadcaster,
    goBack,
    onJoinError,
    onTokenRenewError,
    onChannelConnected,
    onRemoteUserOffline,
  } = args;

  const client = useRTCClient();

  // --- React state (the view-model the page renders) ---
  const [appId, setAppId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  /** Set once the initial token fetch fails — keeps `useJoin` disabled forever. */
  const [tokenFetchFailed, setTokenFetchFailed] = useState(false);
  const [isRenewingToken, setIsRenewingToken] = useState(false);
  const [isOpeningChannel, setIsOpeningChannel] = useState(true);
  const [isMute, setIsMute] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [clearScreen, setClearScreen] = useState(false);
  /**
   * Number of available cameras, surfaced reactively so the controls re-render
   * once enumeration completes. A ref alone would not trigger a re-render.
   */
  const [cameraCount, setCameraCount] = useState(0);

  // --- Refs read from one-shot / cleanup contexts (always the latest value) ---
  /**
   * Mirrors `useJoin`'s resolved local uid (`data: UID`) so the CONNECTED handler
   * reads the LATEST value — the resolved uid may land on a different tick than
   * the `connection-state-change → CONNECTED` event. The web analog of Flutter's
   * `connection.localUid` (`:426`) and the authoritative `broadcasterStreamingId`
   * source (finding 4).
   */
  const joinedUidRef = useRef<number | null>(null);
  /** Guards `onChannelConnected` so it fires exactly once with a real uid. */
  const channelConnectedFiredRef = useRef(false);
  /** Guards the once-only `setClientRole` promotion. */
  const clientRoleSetRef = useRef(false);
  /** Whether the local tracks/channel have already been released (idempotent). */
  const releasedRef = useRef(false);
  /**
   * Latest local tracks, mirrored into refs so the stable `releaseStreaming`
   * always operates on the CURRENT tracks rather than a stale closure captured at
   * mount (when the tracks are still `null`).
   */
  const localCameraTrackRef = useRef<ICameraVideoTrack | null>(null);
  const localMicrophoneTrackRef = useRef<IMicrophoneAudioTrack | null>(null);
  /**
   * The session's available cameras, enumerated ONCE after a local camera track
   * first exists (a track implies camera permission was granted, so the device
   * list is populated with labels). `switchCamera` reads from this cache instead
   * of re-enumerating per tap.
   */
  const camerasRef = useRef<MediaDeviceInfo[]>([]);
  const camerasEnumeratedRef = useRef(false);

  // --- Agora declarative hooks ---
  // Join is gated on a successfully fetched token (Flutter aborts before
  // `joinChannel` when the token is null, `:204-210`). `uid: 0` = auto-assign.
  const joinReady = appId != null && token != null && !tokenFetchFailed;
  // The broadcaster requests + publishes local media; the audience needs NONE
  // (Flutter `enableLocalVideo(_isBroadcaster)` / `enableLocalAudio(_isBroadcaster)`,
  // `:328-330`). The web omits the audience's local-track request entirely (a web
  // deviation from Flutter's always-`enableVideo`; documented at Task 14's
  // launch util).
  const localMediaReady = joinReady && isBroadcaster;

  // Bind the resolved local uid (`useJoin`'s `data`) — the value `useJoin`
  // resolves from the underlying `client.join()` promise, the web analog of
  // Flutter's `connection.localUid` (`:426`). Reading the real server-assigned
  // uid from here (rather than `client.uid`, `undefined` until join completes)
  // guarantees the broadcaster persists the correct `broadcasterStreamingId` the
  // audience matches against the broadcaster's remote stream (finding 4).
  const { data: joinedUid } = useJoin(
    {
      appid: appId ?? '',
      channel,
      token: token ?? null,
      uid: LOCAL_STREAMING_UID,
    },
    joinReady,
  );

  // Local tracks are created only for the broadcaster once the join is armed.
  const { localCameraTrack } = useLocalCameraTrack(localMediaReady);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(localMediaReady);

  // Publish both tracks once they exist and the join is armed — BROADCASTER ONLY
  // (the audience publishes nothing, `:326-327`).
  usePublish([localMicrophoneTrack, localCameraTrack], localMediaReady);

  const remoteUsers = useRemoteUsers();

  // Role: `setClientRole(_isBroadcaster ? clientRoleBroadcaster : clientRoleAudience)`
  // (`:310-315`). A `mode: "live"` client defaults to `audience`; promote to
  // `host` for the broadcaster before publishing. Audience is `'audience'` (NOT
  // `'host'` like the 1:1 video call). A rejection is logged, non-terminal
  // (mirrors `useVideoCall.setRole`).
  useEffect(() => {
    if (!joinReady || clientRoleSetRef.current) {
      return;
    }
    clientRoleSetRef.current = true;
    void client
      .setClientRole(isBroadcaster ? 'host' : 'audience')
      .catch((error: unknown) => {
        appLogger.error('setClientRole failed', 'useLiveStreamingAgora.setRole', {
          error: error as Error,
        });
      });
  }, [joinReady, client, isBroadcaster]);

  // Keep mirroring refs in sync with their state / the latest tracks.
  useEffect(() => {
    localCameraTrackRef.current = localCameraTrack ?? null;
  }, [localCameraTrack]);
  useEffect(() => {
    localMicrophoneTrackRef.current = localMicrophoneTrack ?? null;
  }, [localMicrophoneTrack]);
  useEffect(() => {
    // `data` is `UID = string | number`; the join requests numeric (`uid: 0`), so
    // coerce. `null` until `useJoin` resolves the join.
    if (joinedUid == null) {
      joinedUidRef.current = null;
      return;
    }
    joinedUidRef.current =
      typeof joinedUid === 'number' ? joinedUid : Number(joinedUid);
  }, [joinedUid]);

  // Enumerate the session's cameras ONCE, after the local camera track first
  // exists (broadcaster only — the track implies camera permission, so
  // `getCameras()` returns the full labelled list). Surfaced as `cameraCount` so
  // the controls re-render once cameras are known.
  useEffect(() => {
    if (localCameraTrack == null || camerasEnumeratedRef.current) {
      return;
    }
    camerasEnumeratedRef.current = true;
    void (async () => {
      try {
        const cameras = await AgoraRTC.getCameras();
        camerasRef.current = cameras;
        setCameraCount(cameras.length);
      } catch (error) {
        appLogger.error(
          'getCameras failed',
          'useLiveStreamingAgora.enumerateCameras',
          { error: error as Error },
        );
      }
    })();
  }, [localCameraTrack]);

  // ---------------------------------------------------------------------------
  // Token fetch (`generateLiveStreamingToken`, `:577-586`) — role-based. Returns
  // the fresh token or `null`; `null`/missing `token` → the caller surfaces the
  // failure via `onJoinError` / `onTokenRenewError` (the hook stays UI-free).
  // ---------------------------------------------------------------------------
  const fetchToken = useCallback(async (): Promise<string | null> => {
    try {
      const auth = await generateLiveStreamTokenUseCase.execute({
        channel,
        uid: LOCAL_STREAMING_UID,
        role: isBroadcaster
          ? LiveStreamingRole.broadcaster
          : LiveStreamingRole.audience,
      });
      if (auth == null || auth.token == null) {
        return null;
      }
      setAppId(auth.appId);
      return auth.token;
    } catch (error) {
      appLogger.error(
        'generateLiveStreamToken failed',
        'useLiveStreamingAgora.fetchToken',
        { error: error as Error },
      );
      return null;
    }
  }, [channel, isBroadcaster]);

  // ---------------------------------------------------------------------------
  // Streaming teardown (`disposeStreaming`, `:248-254`). On the web this MUST
  // release the camera/mic (the browser does NOT auto-release): stop + close the
  // local tracks, then `client.leave()`. Idempotent.
  // ---------------------------------------------------------------------------
  const releaseStreaming = useCallback(async () => {
    if (releasedRef.current) {
      return;
    }
    releasedRef.current = true;
    try {
      // Read the LATEST tracks off the refs (a stale closure captured at mount
      // would hold `null`). Stop releases the device; close frees it.
      localMicrophoneTrackRef.current?.stop();
      localMicrophoneTrackRef.current?.close();
      localCameraTrackRef.current?.stop();
      localCameraTrackRef.current?.close();
      await client.leave();
    } catch (error) {
      appLogger.error(
        'releaseStreaming failed',
        'useLiveStreamingAgora.releaseStreaming',
        { error: error as Error },
      );
    }
  }, [client]);

  // ---------------------------------------------------------------------------
  // Join success + resolved-uid dance (`onJoinChannelSuccess`, `:421-445`;
  // finding 4 ported from `useVideoCall`). Clear `_isOpeningChannel` and fire
  // `onChannelConnected` with the RESOLVED uid exactly once. NEVER fire with the
  // `0` auto-assign sentinel — re-arm and retry once the real uid lands.
  // ---------------------------------------------------------------------------
  const handleChannelConnected = useCallback(() => {
    if (channelConnectedFiredRef.current) {
      return;
    }
    channelConnectedFiredRef.current = true;

    // The authoritative streaming id is the resolved join uid (Flutter's
    // `connection.localUid`, `:426`) — NOT `client.uid`, which is `undefined`
    // until the join completes and falls back to the `0` sentinel the audience
    // can never match (finding 4). Fall back to `client.uid` only if it is itself
    // a real non-zero uid.
    const resolvedUid = joinedUidRef.current;
    const localUid =
      resolvedUid != null && resolvedUid !== 0
        ? resolvedUid
        : typeof client.uid === 'number' && client.uid !== 0
          ? client.uid
          : null;

    if (localUid == null) {
      // The resolved uid is not known yet — re-arm the one-shot guard and bail so
      // a later tick (once `useJoin` resolves the uid) re-attempts the fire,
      // rather than emitting `0`. Do NOT clear `_isOpeningChannel` on this branch.
      channelConnectedFiredRef.current = false;
      return;
    }

    setIsOpeningChannel(false);
    onChannelConnected?.(localUid);
  }, [client, onChannelConnected]);

  // ---------------------------------------------------------------------------
  // Token renewal lifecycle (`:451-508`).
  // ---------------------------------------------------------------------------

  /**
   * `renewToken` / `onTokenPrivilegeWillExpire` (`:488-508`): re-mint; null →
   * `onTokenRenewError` + back; else swap the token state + `client.renewToken`.
   * The will-expire path does NOT raise the renewing overlay (parity: Flutter
   * only sets `_isRenewingLiveStreamingToken` on the expired/invalid paths).
   */
  const renewToken = useCallback(async () => {
    const fresh = await fetchToken();
    if (fresh == null) {
      onTokenRenewError?.();
      goBack();
      return;
    }
    setToken(fresh);
    try {
      await client.renewToken(fresh);
    } catch (error) {
      appLogger.error(
        'client.renewToken failed',
        'useLiveStreamingAgora.renewToken',
        { error: error as Error },
      );
    }
  }, [fetchToken, client, goBack, onTokenRenewError]);

  /**
   * `onTokenExpired` + `onInvalidToken` (`:451-484`): raise the renewing overlay,
   * re-mint; null → `onTokenRenewError` + back; else re-join via a token-state
   * swap, then clear the flag.
   *
   * Deviation note: Flutter splits this across three `connectionChangedReason`
   * values (`...TokenExpired` / `...RenewToken` / `...InvalidToken`, `:366-375`)
   * the web SDK 4.x does not expose verbatim; the web SDK consolidates them into
   * the `token-privilege-did-expire` event (the connection is gone → a re-join is
   * required, mirroring Flutter's invalid-token `joinChannel`, `:480`).
   */
  const handleTokenDidExpire = useCallback(async () => {
    setIsRenewingToken(true);
    const fresh = await fetchToken();
    if (fresh == null) {
      onTokenRenewError?.();
      goBack();
      return;
    }
    try {
      // Re-join with the fresh token (`joinChannel`, `:480`). `useJoin` owns the
      // connection lifecycle declaratively, so swapping the token state re-runs
      // the join ONCE. Do NOT also call `client.join(...)` here — a manual join on
      // the already-(re)connecting client throws `INVALID_OPERATION`.
      setToken(fresh);
    } finally {
      setIsRenewingToken(false);
    }
  }, [fetchToken, goBack, onTokenRenewError]);

  // ---------------------------------------------------------------------------
  // Agora client events.
  // ---------------------------------------------------------------------------

  // Join success: the web SDK reaches `CONNECTED` on `connection-state-change`.
  useClientEvent(client, 'connection-state-change', (curState) => {
    if (curState === 'CONNECTED') {
      handleChannelConnected();
    }
  });

  // The resolved local uid (`useJoin`'s `data`) can arrive on a LATER tick than
  // the CONNECTED event. When the CONNECTED handler ran before the uid resolved,
  // it re-armed the guard and bailed without emitting `0` — re-attempt here once
  // the real uid lands (the internal guard makes this idempotent). Finding 4.
  useEffect(() => {
    if (joinedUidRef.current != null && joinedUidRef.current !== 0) {
      handleChannelConnected();
    }
  }, [joinedUid, handleChannelConnected]);

  // `onUserOffline` (`:520-524`). Forward the leaving uid; the page matches it
  // against `broadcasterStreamingId` to drive the "streaming ended" state.
  useClientEvent(client, 'user-left', (user) => {
    onRemoteUserOffline?.(Number(user.uid));
  });

  // `onTokenPrivilegeWillExpire` → `renewToken` (`:380-382`, `:488-490`).
  useClientEvent(client, 'token-privilege-will-expire', () => {
    void renewToken();
  });

  // Token expired / invalid (`:366-375`, `:451-484`).
  useClientEvent(client, 'token-privilege-did-expire', () => {
    void handleTokenDidExpire();
  });

  // ---------------------------------------------------------------------------
  // Broadcaster control actions (`_streamActions`, `:1464-1635`). All are safe
  // no-ops for the audience (its local tracks are `null`).
  // ---------------------------------------------------------------------------
  const toggleMute = useCallback(async () => {
    if (localMicrophoneTrack == null) {
      return;
    }
    const next = !isMute;
    try {
      // Flutter `muteLocalAudioStream(!_isMute)` (`:1508`).
      await localMicrophoneTrack.setMuted(next);
      setIsMute(next);
    } catch (error) {
      appLogger.error('toggleMute failed', 'useLiveStreamingAgora.toggleMute', {
        error: error as Error,
      });
    }
  }, [localMicrophoneTrack, isMute]);

  const toggleCamera = useCallback(async () => {
    if (localCameraTrack == null) {
      return;
    }
    const nextOff = !isCameraOff;
    try {
      // Flutter `muteLocalVideoStream(!_isCameraOff)` (`:1552`). Web:
      // `setEnabled(false)` when turning the camera OFF.
      await localCameraTrack.setEnabled(!nextOff);
      setIsCameraOff(nextOff);
    } catch (error) {
      appLogger.error(
        'toggleCamera failed',
        'useLiveStreamingAgora.toggleCamera',
        { error: error as Error },
      );
    }
  }, [localCameraTrack, isCameraOff]);

  const switchCamera = useCallback(async () => {
    if (localCameraTrack == null) {
      return;
    }
    // Read from the list enumerated once on init rather than re-enumerating on
    // every tap (camera enumeration is stable for the session). Flutter
    // `_engine!.switchCamera()` (`:1485`).
    const cameras = camerasRef.current;
    if (cameras.length < 2) {
      return;
    }
    try {
      const currentId = localCameraTrack.getTrackLabel();
      const currentIdx = cameras.findIndex((c) => c.label === currentId);
      const next = cameras[(currentIdx + 1) % cameras.length];
      await localCameraTrack.setDevice(next.deviceId);
    } catch (error) {
      appLogger.error(
        'switchCamera failed',
        'useLiveStreamingAgora.switchCamera',
        { error: error as Error },
      );
    }
  }, [localCameraTrack]);

  const toggleClearScreen = useCallback(() => {
    setClearScreen((prev) => !prev);
  }, []);

  // ---------------------------------------------------------------------------
  // Lifecycle bridges (forwarded into the page's `useBasePage` via Task 10).
  // ---------------------------------------------------------------------------

  // `init` (`:201-217`): mint the initial token; `null`/missing token →
  // `onJoinError` + back, never join (`:204-210`). Returns the init-side teardown
  // `useBasePage` runs on unmount (cancels a pending fetch). The `cancelled` guard
  // protects an unmount-before-resolve.
  const onAgoraInit = useCallback((): (() => void) => {
    let cancelled = false;
    void (async () => {
      const fresh = await fetchToken();
      if (cancelled) {
        return;
      }
      if (fresh == null) {
        setTokenFetchFailed(true);
        onJoinError?.();
        goBack();
        return;
      }
      setToken(fresh);
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchToken, onJoinError, goBack]);

  // `dispose` / `disposeStreaming` (`:220-254`) — media half only: release the
  // local tracks + leave the channel (idempotent). The Firestore end-side is the
  // Task-10 orchestration hook's concern.
  const onAgoraDispose = useCallback(() => {
    void releaseStreaming();
  }, [releaseStreaming]);

  return {
    isOpeningChannel,
    isRenewingToken,

    localCameraTrack: localCameraTrack ?? null,
    localMicrophoneTrack: localMicrophoneTrack ?? null,
    remoteUsers,

    isMute,
    isCameraOff,
    clearScreen,
    toggleMute,
    toggleCamera,
    switchCamera,
    toggleClearScreen,
    cameraCount,

    onAgoraInit,
    onAgoraDispose,
  };
};
